// External dependencies
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import CmsEditor from '@/modules/cms/client/components/CmsEditor.component';
import {
  createFaqCategory,
  createFaqEntry,
  deleteFaqCategory,
  deleteFaqEntry,
  getFaqCategories,
  getFaqEntries,
  updateFaqCategory,
  updateFaqEntry,
} from '@/modules/cms/client/api/admin-cms.api';
import { getLocales } from '@/modules/core/client/utils/locales';

const EMPTY_ENTRY = {
  question: '',
  answer: '',
  anchor: '',
  status: 'published',
  featured: false,
  keywords: '',
};

/**
 * Pull the server's message out of a failed request, if it sent one.
 *
 * @param {Error} err - Axios error
 * @param {String} fallback - Message when the server sent none
 * @returns {String}
 */
function errorMessage(err, fallback) {
  const message =
    err && err.response && err.response.data && err.response.data.message;
  return message || fallback;
}

/**
 * Persist a new order after moving the item at `index` by `direction`.
 *
 * Writes only the two items that swapped, as the footer menu manager does.
 *
 * @param {Array} list - Items in display order
 * @param {Number} index - Index being moved
 * @param {Number} direction - -1 (up) or 1 (down)
 * @param {Function} update - `(id, { order }) => Promise`
 * @returns {Promise<Array|null>} Reordered list, or null when at an edge
 */
async function persistMove(list, index, direction, update) {
  const target = index + direction;
  if (target < 0 || target >= list.length) {
    return null;
  }

  const reordered = [...list];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

  const first = Math.min(index, target);
  await Promise.all(
    [reordered[first], reordered[first + 1]].map((item, offset) =>
      update(item._id, { order: first + offset }),
    ),
  );

  return reordered.map((item, position) => ({ ...item, order: position }));
}

/**
 * Admin screen for the FAQ: categories on the left, the selected category's
 * questions on the right, with an inline editor for one question at a time.
 */
export default function AdminCmsFaq() {
  const [locale, setLocale] = useState('en');
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [categoryForm, setCategoryForm] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [entries, setEntries] = useState([]);
  const [entryForm, setEntryForm] = useState(null);

  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const locales = getLocales();
  const selected = categories.find(category => category._id === selectedId);

  const notify = (message, isError = false) => {
    setError(isError ? message : null);
    setSuccess(isError ? null : message);
  };

  const loadCategories = async (keepSelection = true) => {
    setIsLoadingCategories(true);
    try {
      const data = await getFaqCategories({ locale });
      setCategories(data);
      const stillThere =
        keepSelection && data.some(category => category._id === selectedId);
      if (!stillThere) {
        setSelectedId(data.length ? data[0]._id : null);
      }
    } catch (err) {
      notify('Failed to load FAQ categories.', true);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadEntries = async categoryId => {
    if (!categoryId) {
      setEntries([]);
      return;
    }
    setIsLoadingEntries(true);
    try {
      setEntries(await getFaqEntries(categoryId));
    } catch (err) {
      notify('Failed to load questions.', true);
    } finally {
      setIsLoadingEntries(false);
    }
  };

  useEffect(() => {
    setSelectedId(null);
    loadCategories(false);
  }, [locale]);

  useEffect(() => {
    setEntryForm(null);
    loadEntries(selectedId);
    setCategoryForm(
      selected
        ? {
            name: selected.name,
            slug: selected.slug,
            description: selected.description || '',
            enabled: selected.enabled !== false,
          }
        : null,
    );
  }, [selectedId]);

  // Categories ---------------------------------------------------------------

  const handleCreateCategory = async event => {
    event.preventDefault();
    const name = newCategoryName.trim();
    if (!name) {
      return;
    }

    setIsSaving(true);
    try {
      const created = await createFaqCategory({ name, locale });
      setNewCategoryName('');
      setCategories(previous => [...previous, created]);
      setSelectedId(created._id);
      notify(`Category “${created.name}” created.`);
    } catch (err) {
      notify(errorMessage(err, 'Failed to create category.'), true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCategory = async event => {
    event.preventDefault();
    if (!selected || !categoryForm) {
      return;
    }

    setIsSaving(true);
    try {
      const saved = await updateFaqCategory(selected._id, categoryForm);
      setCategories(previous =>
        previous.map(category =>
          category._id === saved._id ? saved : category,
        ),
      );
      notify('Category saved.');
    } catch (err) {
      notify(errorMessage(err, 'Failed to save category.'), true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selected) {
      return;
    }
    if (!window.confirm(`Delete the category “${selected.name}”?`)) {
      return;
    }

    try {
      await deleteFaqCategory(selected._id);
      notify(`Category “${selected.name}” deleted.`);
      setSelectedId(null);
      loadCategories(false);
    } catch (err) {
      // 409 carries the reason (it still holds questions).
      notify(errorMessage(err, 'Failed to delete category.'), true);
    }
  };

  const handleMoveCategory = async (index, direction) => {
    try {
      const reordered = await persistMove(
        categories,
        index,
        direction,
        updateFaqCategory,
      );
      if (reordered) {
        setCategories(reordered);
      }
    } catch (err) {
      notify('Failed to reorder categories.', true);
    }
  };

  // Questions ----------------------------------------------------------------

  const startNewEntry = () => {
    setEntryForm({ ...EMPTY_ENTRY });
    setError(null);
    setSuccess(null);
  };

  const startEditEntry = entry => {
    setEntryForm({
      _id: entry._id,
      question: entry.question,
      answer: entry.answer,
      anchor: entry.anchor,
      status: entry.status,
      featured: Boolean(entry.featured),
      keywords: (entry.keywords || []).join(', '),
    });
    setError(null);
    setSuccess(null);
  };

  const setEntryField = (name, value) => {
    setEntryForm(previous => ({ ...previous, [name]: value }));
  };

  const handleSaveEntry = async event => {
    event.preventDefault();
    if (!entryForm || !selected) {
      return;
    }

    const payload = {
      category: selected._id,
      question: entryForm.question,
      answer: entryForm.answer,
      status: entryForm.status,
      featured: entryForm.featured,
      keywords: entryForm.keywords
        .split(',')
        .map(keyword => keyword.trim())
        .filter(Boolean),
    };
    // An empty anchor on create means "derive it"; on update it is left as is.
    if (entryForm.anchor.trim()) {
      payload.anchor = entryForm.anchor.trim();
    }

    setIsSaving(true);
    try {
      if (entryForm._id) {
        await updateFaqEntry(entryForm._id, payload);
        notify('Question saved.');
      } else {
        await createFaqEntry(payload);
        notify('Question added.');
      }
      setEntryForm(null);
      await loadEntries(selected._id);
      // Keep the category's question count current.
      setCategories(previous =>
        previous.map(category =>
          category._id === selected._id
            ? {
                ...category,
                entryCount: entryForm._id
                  ? category.entryCount
                  : (category.entryCount || 0) + 1,
              }
            : category,
        ),
      );
    } catch (err) {
      notify(errorMessage(err, 'Failed to save question.'), true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEntry = async entry => {
    if (!window.confirm(`Delete “${entry.question}”? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteFaqEntry(entry._id);
      setEntries(previous => previous.filter(item => item._id !== entry._id));
      setCategories(previous =>
        previous.map(category =>
          category._id === selectedId
            ? {
                ...category,
                entryCount: Math.max(0, (category.entryCount || 1) - 1),
              }
            : category,
        ),
      );
      if (entryForm && entryForm._id === entry._id) {
        setEntryForm(null);
      }
      notify('Question deleted.');
    } catch (err) {
      notify('Failed to delete question.', true);
    }
  };

  const handleToggleEntry = async entry => {
    const status = entry.status === 'published' ? 'draft' : 'published';
    try {
      const saved = await updateFaqEntry(entry._id, { status });
      setEntries(previous =>
        previous.map(item => (item._id === saved._id ? saved : item)),
      );
    } catch (err) {
      notify('Failed to change question.', true);
    }
  };

  const handleMoveEntry = async (index, direction) => {
    try {
      const reordered = await persistMove(
        entries,
        index,
        direction,
        updateFaqEntry,
      );
      if (reordered) {
        setEntries(reordered);
      }
    } catch (err) {
      notify('Failed to reorder questions.', true);
    }
  };

  // Render -------------------------------------------------------------------

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-9">
            <h2>FAQ</h2>
            <p>
              Questions are grouped in categories, each served from{' '}
              <code>/faq/&lt;category&gt;</code> with its own search engine
              schema. Only published questions in shown categories appear on the
              site.
            </p>
          </div>
          <div className="col-sm-3" style={{ marginTop: '20px' }}>
            <label htmlFor="admin-faq-locale">Language</label>
            <select
              id="admin-faq-locale"
              className="form-control"
              value={locale}
              onChange={event => setLocale(event.target.value)}
            >
              {locales.map(item => (
                <option key={item.code} value={item.code}>
                  {item.label} ({item.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="row">
          {/* Categories */}
          <div className="col-sm-4">
            <div className="panel panel-default">
              <div className="panel-heading">Categories</div>
              {isLoadingCategories ? (
                <div className="panel-body">Loading…</div>
              ) : (
                <div className="list-group">
                  {categories.map((category, index) => (
                    <div
                      key={category._id}
                      className={classnames('list-group-item', {
                        active: category._id === selectedId,
                      })}
                    >
                      <div className="row">
                        <div className="col-xs-8">
                          <a
                            href={`#category-${category._id}`}
                            className="admin-faq-category-link"
                            onClick={event => {
                              event.preventDefault();
                              setSelectedId(category._id);
                            }}
                          >
                            {category.name}
                          </a>{' '}
                          <span className="badge">{category.entryCount}</span>
                          {category.enabled === false && (
                            <span
                              className="label label-default"
                              style={{ marginLeft: '5px' }}
                            >
                              Hidden
                            </span>
                          )}
                        </div>
                        <div className="col-xs-4 text-right">
                          <div className="btn-group btn-group-xs" role="group">
                            <button
                              type="button"
                              className="btn btn-default"
                              aria-label="Move category up"
                              disabled={index === 0}
                              onClick={() => handleMoveCategory(index, -1)}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="btn btn-default"
                              aria-label="Move category down"
                              disabled={index === categories.length - 1}
                              onClick={() => handleMoveCategory(index, 1)}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!categories.length && (
                    <div className="list-group-item">
                      No categories in this language yet.
                    </div>
                  )}
                </div>
              )}
              <div className="panel-footer">
                <form onSubmit={handleCreateCategory}>
                  <label htmlFor="admin-faq-new-category" className="sr-only">
                    New category name
                  </label>
                  <div className="input-group">
                    <input
                      id="admin-faq-new-category"
                      type="text"
                      className="form-control"
                      value={newCategoryName}
                      onChange={event => setNewCategoryName(event.target.value)}
                      placeholder="New category name"
                    />
                    <span className="input-group-btn">
                      <button
                        type="submit"
                        className="btn btn-default"
                        disabled={isSaving || !newCategoryName.trim()}
                      >
                        Add
                      </button>
                    </span>
                  </div>
                </form>
              </div>
            </div>

            {selected && categoryForm && (
              <form
                className="panel panel-default"
                onSubmit={handleSaveCategory}
              >
                <div className="panel-heading">Edit category</div>
                <div className="panel-body">
                  <div className="form-group">
                    <label htmlFor="admin-faq-category-name">Name</label>
                    <input
                      id="admin-faq-category-name"
                      type="text"
                      className="form-control"
                      value={categoryForm.name}
                      onChange={event =>
                        setCategoryForm({
                          ...categoryForm,
                          name: event.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin-faq-category-slug">Slug</label>
                    <input
                      id="admin-faq-category-slug"
                      type="text"
                      className="form-control"
                      value={categoryForm.slug}
                      onChange={event =>
                        setCategoryForm({
                          ...categoryForm,
                          slug: event.target.value,
                        })
                      }
                      required
                    />
                    <span className="help-block">
                      Public URL: <code>/faq/{categoryForm.slug}</code>
                    </span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="admin-faq-category-description">
                      Description
                    </label>
                    <textarea
                      id="admin-faq-category-description"
                      className="form-control"
                      rows="2"
                      value={categoryForm.description}
                      onChange={event =>
                        setCategoryForm({
                          ...categoryForm,
                          description: event.target.value,
                        })
                      }
                      placeholder="Optional one-liner shown under the heading"
                    />
                  </div>
                  <div className="checkbox">
                    <label htmlFor="admin-faq-category-enabled">
                      <input
                        id="admin-faq-category-enabled"
                        type="checkbox"
                        checked={categoryForm.enabled}
                        onChange={event =>
                          setCategoryForm({
                            ...categoryForm,
                            enabled: event.target.checked,
                          })
                        }
                      />
                      Shown on the site
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSaving}
                    style={{ marginRight: '5px' }}
                  >
                    Save category
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteCategory}
                  >
                    Delete
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Questions */}
          <div className="col-sm-8">
            {!selected ? (
              <p className="text-muted">
                Add a category to start adding questions.
              </p>
            ) : (
              <>
                <div className="row" style={{ marginBottom: '1rem' }}>
                  <div className="col-sm-8">
                    <h3 style={{ marginTop: 0 }}>
                      Questions in “{selected.name}”
                    </h3>
                  </div>
                  <div className="col-sm-4 text-right">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={startNewEntry}
                      disabled={Boolean(entryForm && !entryForm._id)}
                    >
                      New question
                    </button>
                  </div>
                </div>

                {entryForm && (
                  <form
                    className="panel panel-primary"
                    onSubmit={handleSaveEntry}
                  >
                    <div className="panel-heading">
                      {entryForm._id ? 'Edit question' : 'New question'}
                    </div>
                    <div className="panel-body">
                      <div className="form-group">
                        <label htmlFor="admin-faq-question">Question</label>
                        <input
                          id="admin-faq-question"
                          type="text"
                          className="form-control"
                          value={entryForm.question}
                          onChange={event =>
                            setEntryField('question', event.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="admin-faq-answer">Answer</label>
                        <CmsEditor
                          id="admin-faq-answer"
                          value={entryForm.answer}
                          placeholder="Write the answer"
                          onChange={value => setEntryField('answer', value)}
                        />
                        <span className="help-block">
                          Markup is sanitized on save, as for pages.
                        </span>
                      </div>
                      <div className="row">
                        <div className="col-sm-6">
                          <div className="form-group">
                            <label htmlFor="admin-faq-anchor">Anchor</label>
                            <input
                              id="admin-faq-anchor"
                              type="text"
                              className="form-control"
                              value={entryForm.anchor}
                              onChange={event =>
                                setEntryField('anchor', event.target.value)
                              }
                              placeholder="Left blank, derived from the question"
                            />
                            <span className="help-block">
                              Deep link:{' '}
                              <code>
                                /faq/{selected.slug}#{selected.slug}-
                                {entryForm.anchor || '…'}
                              </code>
                            </span>
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="form-group">
                            <label htmlFor="admin-faq-keywords">Keywords</label>
                            <input
                              id="admin-faq-keywords"
                              type="text"
                              className="form-control"
                              value={entryForm.keywords}
                              onChange={event =>
                                setEntryField('keywords', event.target.value)
                              }
                              placeholder="homestay, host family, ireland"
                            />
                            <span className="help-block">
                              Comma separated. Optional search terms.
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="checkbox">
                        <label htmlFor="admin-faq-published">
                          <input
                            id="admin-faq-published"
                            type="checkbox"
                            checked={entryForm.status === 'published'}
                            onChange={event =>
                              setEntryField(
                                'status',
                                event.target.checked ? 'published' : 'draft',
                              )
                            }
                          />
                          Published
                        </label>
                      </div>
                      <div className="checkbox">
                        <label htmlFor="admin-faq-featured">
                          <input
                            id="admin-faq-featured"
                            type="checkbox"
                            checked={entryForm.featured}
                            onChange={event =>
                              setEntryField('featured', event.target.checked)
                            }
                          />
                          Featured — listed under “Top questions” on the FAQ
                          landing page
                        </label>
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={
                          isSaving || !entryForm.question || !entryForm.answer
                        }
                        style={{ marginRight: '5px' }}
                      >
                        {isSaving ? 'Saving…' : 'Save question'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => setEntryForm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {isLoadingEntries ? (
                  <p>Loading questions…</p>
                ) : (
                  <table className="table table-striped admin-faq-entries">
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>State</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, index) => (
                        <tr key={entry._id}>
                          <td>
                            {entry.question}
                            {entry.featured && (
                              <span
                                className="label label-info"
                                style={{ marginLeft: '5px' }}
                              >
                                Featured
                              </span>
                            )}
                          </td>
                          <td>
                            {entry.status === 'published' ? (
                              <span className="label label-success">
                                Published
                              </span>
                            ) : (
                              <span className="label label-default">Draft</span>
                            )}
                          </td>
                          <td className="admin-faq-actions">
                            <div
                              className="btn-group btn-group-xs"
                              role="group"
                              style={{ marginRight: '5px' }}
                            >
                              <button
                                type="button"
                                className="btn btn-default"
                                aria-label="Move question up"
                                disabled={index === 0}
                                onClick={() => handleMoveEntry(index, -1)}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="btn btn-default"
                                aria-label="Move question down"
                                disabled={index === entries.length - 1}
                                onClick={() => handleMoveEntry(index, 1)}
                              >
                                ↓
                              </button>
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary btn-xs"
                              style={{ marginRight: '5px' }}
                              onClick={() => startEditEntry(entry)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-default btn-xs"
                              style={{ marginRight: '5px' }}
                              onClick={() => handleToggleEntry(entry)}
                            >
                              {entry.status === 'published'
                                ? 'Unpublish'
                                : 'Publish'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-xs"
                              onClick={() => handleDeleteEntry(entry)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!entries.length && (
                        <tr>
                          <td colSpan="3">
                            No questions in this category yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

AdminCmsFaq.propTypes = {};
